import { NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  booleanAttribute,
  Component,
  computed,
  contentChild,
  contentChildren,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
} from '@angular/core';
import { Grid, GridCell, GridCellWidget, GridRow } from '@angular/aria/grid';

import { watchSize } from '../../utils/dom.utils';
import { MibCardDirective } from '../card';
import { MibTooltipDirective } from '../tooltip';
import { MibTableCellDirective } from './table-cell.directive';
import { MibTableClippedDirective } from './table-clipped.directive';
import { MibTableSectionEndDirective } from './table-section-end.directive';
import { MibTableWidgetsService } from './table-widgets.service';
import {
  MIB_TABLE_ALIGN,
  MIB_TABLE_HEADER_HEIGHT,
  MibTableColumn,
  MibTableHeaderSize,
  MibTableRowLink,
  MibTableRowOpened,
  MibTableSection,
} from './table.models';
import { MibTableEntry, MibTableGroup } from './table-entry.models';

/** The narrowest the giving-way column is squeezed to: below that it has stopped being a column. */
const GROWING_FLOOR = '8rem';

/*
 Long enough for the measurement of the pointed cell to land before the tooltip opens, and long
 enough that sweeping across a table does not flash one tooltip per column on the way.
*/
const TOOLTIP_DELAY = 400;

/* The disabled look lands on what a disabled cell holds, never on the cell: an ancestor's opacity cannot
   be undone. A cell holding a `mibCellWidget` is not disabled. Written whole, or Tailwind never sees it. */
const DISABLED_ROW = '[&>[aria-disabled=true]>*]:opacity-40 [&>[aria-disabled=true]>*]:cursor-not-allowed';

/** The key of the rows drawn under no band, which no band key can equal. */
const UNSECTIONED = Symbol('unsectioned');

/**
 * A list in columns: a heading line, then one row per thing.
 *
 * The columns are **data** the page hands over — a key, a label, a size, what a long content does,
 * a type, where it sits — so the list of them lives with the page's configuration and grows without
 * a template changing. A cell shows `row[key]` as text unless the page writes it, with an
 * `<ng-template mibCell="key">` that can hold anything: a picture, a badge, a menu.
 *
 * ```html
 * <mib-table
 *   [columns]="columns"
 *   [rows]="cases()"
 *   [rowLink]="{ link: 'row.href', label: 'row.title' }"
 *   (rowOpened)="open($event)"
 *   aria-label="Cases">
 *   <ng-template mibCell="title" let-c>{{ c.title }}</ng-template>
 * </mib-table>
 * ```
 *
 * It is a **grid** and not a static table, which is what `@angular/aria/grid` gives it: rows hold
 * links and menus, so the whole table is one tab stop and the arrow keys walk it. A list of fifty
 * cases is three tab stops on the page instead of a hundred and fifty.
 *
 * The order of the rows is the page's — the table draws them as handed over and never sorts. So is
 * everything around it: its width, its place, and whatever is written underneath.
 */
@Component({
  selector: 'mib-table',
  imports: [
    NgTemplateOutlet,
    Grid,
    GridRow,
    GridCell,
    GridCellWidget,
    MibCardDirective,
    MibTooltipDirective,
    MibTableClippedDirective,
  ],
  templateUrl: './table.component.html',
  providers: [MibTableWidgetsService],
  host: { class: 'block' },
})
export class MibTableComponent<T = unknown> {
  /** The columns, in the order they are drawn. */
  readonly columns = input.required<readonly MibTableColumn[]>();
  /** The things to list, drawn in the order handed over. */
  readonly rows = input.required<readonly T[]>();
  /** What identifies a row across refreshes. Left out, the row object itself. */
  readonly rowKey = input<((row: T) => unknown) | null>(null);
  /** Accessible name of the table, e.g. "Cases". */
  readonly ariaLabel = input.required<string>({ alias: 'aria-label' });
  /** Makes the whole row a link. Left out, a row is not clickable. */
  readonly rowLink = input<MibTableRowLink | null>(null);
  /** Greys out the rows it returns true for and drops their link; the controls in their cells stay usable. */
  readonly rowDisabled = input<((row: T) => boolean) | null>(null);
  /** The section each row belongs to; a band is drawn before a row whose label differs from the previous one. */
  readonly rowSection = input<((row: T) => MibTableSection | null) | null>(null);
  /** What is written in place of the rows when there are none. */
  readonly emptyLabel = input('Nothing to show.');
  /** Drops the heading line, for a table whose columns name themselves. */
  readonly headless = input(false, { transform: booleanAttribute });
  /** Height of the heading line, on the control scale: `sm` 32 · `md` 36 · `lg` 40 · `xl` 44. */
  readonly headerSize = input<MibTableHeaderSize>('md');
  /**
   * A height the table does not grow past — `'24rem'`, `'60vh'`. Beyond it the rows scroll.
   *
   * Without one the table is as tall as its rows and it is the page that scrolls, unless
   * {@link stickyHeader} is set and the host is given a height: the rows then fill what it gets.
   */
  readonly maxHeight = input('');
  /**
   * Keeps the heading in view while the rows scroll. Needs a {@link maxHeight} or a height on the host.
   *
   * It also decides **what scrolls**. Set, the rows scroll in a box of their own and the heading
   * sits outside it, so the scrollbar starts at the first row rather than alongside the column
   * names — a scrollbar always spans the whole of its box. Left out, the whole table scrolls as one
   * and the heading goes up with the rows.
   */
  readonly stickyHeader = input(false, { transform: booleanAttribute });

  /**
   * The reader opened a row in place — a plain left click, modifiers excluded.
   *
   * A ctrl, meta, shift or alt click is the browser's business: it opens a tab, a window or a
   * download, and emitting there would route the page as well and open the thing twice.
   *
   * The event comes with the row: a page that routes in place calls `preventDefault()` on it, one
   * that lets the browser follow the address does not.
   */
  readonly rowOpened = output<MibTableRowOpened<T>>();

  /**
   * The reader pressed a row with ctrl or cmd, asking for it beside the page rather than instead of it.
   * Nothing is prevented: a page that keeps it for itself calls `preventDefault()` on the event, and a
   * page that does not listen leaves the browser to open a tab.
   */
  readonly rowOpenedAside = output<MibTableRowOpened<T>>();

  private readonly cells = contentChildren(MibTableCellDirective<T>);
  protected readonly sectionEnd = contentChild(MibTableSectionEndDirective);

  private readonly widgets = inject(MibTableWidgetsService);

  private readonly grid = viewChild<ElementRef<HTMLElement>>('grid');
  private readonly rowGroup = viewChild<ElementRef<HTMLElement>>('rowGroup');
  private readonly bands = viewChildren<ElementRef<HTMLElement>>('band');
  /* The tallest band drawn, in px; measured only while a heading is kept in view. */
  private readonly bandHeight = signal(0);
  /* A new object on every call, so the same position asked twice scrolls again; applied once, then forgotten. */
  private readonly pendingScroll = signal<{ top: number } | null>(null);
  private lastPending: { top: number } | null = null;

  /** The templates the page wrote, by the key of the column each one fills. */
  protected readonly templates = computed<Record<string, TemplateRef<{ $implicit: T }>>>(() =>
    Object.fromEntries(this.cells().map((cell) => [cell.column(), cell.template])),
  );

  /** The rows as drawn, grouped under a band wherever the section label differs from the previous row's. */
  protected readonly groups = computed<readonly MibTableGroup<T>[]>(() => {
    const sectionOf = this.rowSection();
    if (!sectionOf) return this.rows().length ? [this.unsectioned()] : [];
    return this.withSections(sectionOf);
  });

  /*
   One track per column, declared once on the table: a column is a track, so its width is decided
   across every row **and the heading**, which is what a row of flex boxes could never do — there,
   each cell sized itself and a column drifted from one row to the next.

   `max-content` is the floor of every track, so no column is ever squeezed below the larger of its
   heading and its longest cell, and nothing has to be hidden as the table narrows. A width the page
   asked for is kept only above that floor: in `minmax(max-content, 7rem)` the 7rem is dropped the
   moment the content needs more. The `100%` column is the exception and the only one that gives
   way — it shrinks first, which is where an ellipsis happens. It stops at a floor of its own: a
   column squeezed to nothing has disappeared, which is the thing this layout exists to prevent.
   Past that the table scrolls sideways rather than clipping what it cannot fit.

   Room to spare is shared, the way a `<table>` at full width shares it: an `auto` column takes its
   content and then an equal part of what is left. That is `auto` as the ceiling and not `1fr` —
   `1fr` hands the whole surplus to one column, so beside a closed side panel a title column was
   stretching far past its longest title while the others were pushed against the far edge.

   Two kinds of column stay out of the sharing. A width the page asked for is held at that width.
   And `fit-content` is exactly as wide as what it holds — a column of controls, whose button has to
   stay on the edge rather than walk away from it as the table widens.
  */
  protected readonly tracks = computed(() =>
    this.columns()
      .map((column) => {
        const size = column.size ?? 'auto';
        switch (size) {
          case '100%':
            return `minmax(${GROWING_FLOOR}, auto)`;
          case 'auto':
            return 'minmax(max-content, auto)';
          case 'fit-content':
            return 'max-content';
          default:
            return `minmax(max-content, ${size})`;
        }
      })
      .join(' '),
  );

  /*
   `overflow-x-auto` already makes the table its own scroll container on both axes — CSS turns the
   other axis to `auto` the moment one is not `visible`. What it lacks without a height is a reason
   to scroll vertically: the grid is as tall as its rows, so the page scrolls instead.

   With a heading kept in view, **the height may also come from the page** rather than from an input.
   `max-h-full` lets the grid stop at whatever the host was given — a flex child, a cell of another
   grid, anything the page already knows how to size — and the two rows are declared so the heading
   takes what it needs and the group of rows takes the rest and no more. That `minmax(0, 1fr)` is
   what lets it be shorter than its content; `1fr` alone has a floor of `auto` and would not shrink.

   `auto-rows-min` is for the other branch, where the whole table scrolls: a grid stretches its rows
   over a fixed height, which would blow them up instead of letting them overflow.
  */
  protected readonly gridClasses = computed(
    () =>
      'mib-scrollbar grid overflow-x-auto ' +
      'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mib-primary ' +
      (this.stickyHeader() ? 'max-h-full grid-rows-[auto_minmax(0,1fr)]' : this.maxHeight() ? 'auto-rows-min' : ''),
  );

  /*
   Where the height lands decides what scrolls, and that is the whole of the sticky heading. On the
   table, the heading scrolls away with the rows. On the group of rows, the heading is outside the
   box that scrolls, so the scrollbar starts at the first row instead of running past the column
   names — a scrollbar spans the whole of its box and there is no way to shorten one.

   `null` rather than an empty string, or the style stays on the element as `max-height: `.
  */
  protected readonly outerHeight = computed(() => (this.stickyHeader() ? null : this.maxHeight() || null));

  protected readonly bodyHeight = computed(() => (this.stickyHeader() ? this.maxHeight() || null : null));

  /* Scrolls a focused row to below the sticky band rather than under it. */
  protected readonly bandPadding = computed(() => {
    if (!this.stickyHeader() || !this.bands().length) return null;
    return this.bandHeight() || null;
  });

  /*
   A subgrid again, so the rows keep landing on the table's own tracks with a box in between — the
   columns of the heading and those of the rows are still one set of tracks, which is what stopped
   them drifting apart in the first place.

   It scrolls on the whole sticky branch and not only when a length was given, because the height
   may be coming from the page. `min-h-0` is what allows that: a grid item refuses to go below its
   content without it, so the group would have pushed the grid past the height it was given.

   `overflow-x-clip` stops a vertical scrollbar from bringing a horizontal one: the bar takes its
   width out of this box while the rows stay on tracks sized on the table, so they overflow by
   exactly that width. `clip` takes it out of the last cell's inset instead and draws no bar;
   scrolling sideways is the grid's business, not this box's.
  */
  protected readonly bodyClasses = computed(
    () =>
      'mib-scrollbar col-span-full grid grid-cols-subgrid' +
      (this.stickyHeader() ? ' min-h-0 overflow-x-clip overflow-y-auto' : this.bodyHeight() ? ' overflow-y-auto' : ''),
  );

  /*
   A row is a subgrid, so it lines up on the table's own tracks while keeping a box of its own —
   which `display: contents` would have taken away, and with it the ground the row link is laid on.
   It carries no horizontal padding for the same reason: padding on a subgrid shifts its content
   box and the column would no longer line up. The inset lives on the first and last cell instead,
   where it leaves the row's background spanning the whole width.
  */
  /*
   The height is the row's and not the cell's padding: taken from the padding it would follow the
   line-height of the micro role and no input could reach it, which is the whole point of the
   control scale — a heading sits in a row of controls and reads as one of them.

   A heading kept in view sits outside the box that scrolls, so it needs no `sticky` of its own —
   nothing moves it. It still carries its own ground and is raised above the rows: the table scrolls
   sideways as one, and a row passing under the heading on that axis would otherwise show through.
  */
  protected readonly headingRowClasses = computed(
    () =>
      'col-span-full grid grid-cols-subgrid items-center gap-x-3 border-b border-mib-divider ' +
      `${MIB_TABLE_HEADER_HEIGHT[this.headerSize()]} ` +
      'text-mib-micro font-semibold tracking-widest text-gray-500 uppercase ' +
      (this.stickyHeader() ? 'relative z-20 bg-white' : ''),
  );

  protected readonly rowLinkClasses =
    'absolute inset-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mib-primary';

  protected readonly emptyClasses = 'col-span-full px-3 py-8 text-center text-xs/4.5 text-gray-500';

  protected readonly tooltipDelay = TOOLTIP_DELAY;

  /*
   The row is the positioned ancestor the row link covers, and everything pressable in a cell is
   raised above it: a static element sits under an absolutely positioned one whatever the source
   order, so a favourite or a menu would answer the row instead of itself.

   A badge in a cell is **pinned**, for the reason the case card pins the ones in its lines:
   `mibBadge` is sized in `em`, so left alone a kind comes out at 10.5px in a row and a mark under a
   name at 9px, both under the floor Typography sets. Pinned, they are the metadata size at 600 in a
   24px pill. The skin stays the badge's; only the geometry is taken over.
  */
  private readonly rowClasses =
    'relative col-span-full grid grid-cols-subgrid items-center gap-x-3 ' +
    'border-b border-mib-divider last:border-b-0 text-sm text-gray-900 ' +
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mib-primary ' +
    '[&_button]:relative [&_button]:z-10 [&_[mibCellWidget]]:relative [&_[mibCellWidget]]:z-10 ' +
    '[&_[mibBadge]]:h-6 [&_[mibBadge]]:min-w-6 [&_[mibBadge]]:gap-1 ' +
    '[&_[mibBadge]]:px-2 [&_[mibBadge]]:text-xs [&_[mibBadge]]:font-semibold';

  /* A band is sticky within its group, so the next group's band pushes it out. A box, never `display: contents`,
     or the band sticks to the whole scroll box. The last row of a group keeps its line unless the group is last. */
  protected readonly groupClasses = 'col-span-full grid grid-cols-subgrid not-last:*:last:border-b';

  /* A band spans every column and is not a data row: no link, no hover. Its ground is the page's,
     as an inset band inside a panel, so the metadata grey keeps its contrast on it. */
  protected readonly sectionRowClasses = computed(
    () =>
      'col-span-full grid grid-cols-subgrid border-b border-mib-divider bg-mib-app-background' +
      // The scroll box is the group of rows, under the heading: `top-0` whatever its size, `z-20` over the widgets.
      (this.stickyHeader() ? ' sticky top-0 z-20' : ''),
  );

  /* `contain-inline-size` keeps a long band from widening the columns: it wraps inside the row instead. */
  protected readonly sectionCellClasses =
    'col-span-full flex min-w-0 contain-inline-size items-center p-3 ' +
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mib-primary';

  constructor() {
    afterRenderEffect({
      write: () => {
        const pending = this.pendingScroll();
        if (!pending || pending === this.lastPending) return;
        if (!this.rows().length) return;
        this.lastPending = pending;
        const box = this.scrollBox();
        if (!box) return;
        box.scrollTop = pending.top;
      },
    });
    afterRenderEffect((onCleanup) => {
      if (!this.stickyHeader()) return;
      const stops = this.bands().map((band) => watchSize(band.nativeElement, () => this.measureBands()));
      onCleanup(() => stops.forEach((stop) => stop()));
    });
  }

  /** How far the rows are scrolled, in px; 0 when the page scrolls rather than the table. */
  scrollPosition(): number {
    return this.scrollBox()?.scrollTop ?? 0;
  }

  /** Scrolls the rows to `top` px, once, after the next render that has rows; nothing when the page scrolls. */
  scrollToPosition(top: number): void {
    this.pendingScroll.set({ top });
  }

  /** The identity a row is tracked by: its key, or the row itself when there is no key. */
  protected key(row: T): unknown {
    return this.rowKey()?.(row) ?? row;
  }

  /** What a cell shows when the page wrote no template for its column: the field, as text. */
  protected text(row: T, column: MibTableColumn): string {
    return this.field(row, column.key);
  }

  /** Whether the page greyed this row out. */
  protected disabled(row: T): boolean {
    return this.rowDisabled()?.(row) ?? false;
  }

  /** A row's classes: a disabled row is dimmed and never lit under the pointer. */
  protected rowClassesOf(row: T): string {
    if (this.disabled(row)) return `${this.rowClasses} ${DISABLED_ROW}`;
    if (this.rowLink()) return `${this.rowClasses} hover:bg-gray-100`;
    return this.rowClasses;
  }

  /**
   * Whether a cell of a disabled row is disabled: never one holding a `mibCellWidget`, since the grid
   * disables the widgets of a disabled cell. The grid stays soft-disabled, so the arrow keys still reach it.
   */
  protected cellDisabled(row: T, cell: GridCell): boolean {
    return this.disabled(row) && !this.widgets.holds(cell);
  }

  /** The address of a row, or an empty string for a row that is not a link. */
  protected href(row: T): string {
    const link = this.rowLink();
    if (!link || this.disabled(row)) return '';
    return this.field(row, link.link);
  }

  /** What names the row for someone who does not see it, the anchor holding no text of its own. */
  protected name(row: T): string {
    const path = this.rowLink()?.label ?? this.columns()[0]?.key;
    return path ? this.field(row, path) : '';
  }

  protected open(row: T, event: MouseEvent): void {
    if (event.button !== 0 || event.shiftKey || event.altKey) return;
    if (event.ctrlKey || event.metaKey) this.rowOpenedAside.emit({ row, event });
    else this.rowOpened.emit({ row, event });
  }

  /*
   `overflow` is the column's and reaches every cell of it, written by the page or not. The ellipsis
   lands on the children rather than on the cell, for two reasons: a flex container never shows one
   of its own, and a cell that clipped would cut the row link down to its own width.

   `align` is the column's too and reaches its heading as well, so a column reads as one thing. The
   type only decides its default: figures are ranged `end`, where they line up down the column.
  */
  protected cellClasses(column: MibTableColumn): string {
    return [
      'flex min-w-0 gap-2 py-2 first:pl-3 last:pr-3',
      column.overflow === 'wrap'
        ? 'items-start [&>*]:min-w-0 [&>*]:text-pretty'
        : 'items-center whitespace-nowrap [&>*]:min-w-0 [&>*]:truncate',
      MIB_TABLE_ALIGN[column.align ?? (column.type === 'number' ? 'end' : 'start')],
      column.type === 'number' ? 'tabular-nums' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  /* A band is tracked by a symbol no row key can equal, kept from one pass to the next for the same band. */
  private bandKeys = new Map<string, symbol>();

  /** Opens a group on each row whose section label differs from the previous row's; a row with none stays put. */
  private withSections(sectionOf: (row: T) => MibTableSection | null): MibTableGroup<T>[] {
    const groups: { key: symbol; section: MibTableSection | null; rows: MibTableEntry<T>[] }[] = [];
    const repeats = new Map<string, number>();
    const keys = new Map<string, symbol>();
    let previous: string | null = null;
    for (const row of this.rows()) {
      const section = sectionOf(row);
      if (section && section.label !== previous) {
        const count = repeats.get(section.label) ?? 0;
        repeats.set(section.label, count + 1);
        const id = `${section.label}:${count}`;
        const key = this.bandKeys.get(id) ?? Symbol(id);
        keys.set(id, key);
        groups.push({ key, section, rows: [] });
      }
      if (!groups.length) groups.push({ key: UNSECTIONED, section: null, rows: [] });
      previous = section?.label ?? null;
      groups[groups.length - 1].rows.push(this.rowEntry(row));
    }
    this.bandKeys = keys;
    return groups;
  }

  /** The box the rows scroll in: the group of rows under a kept heading, the grid under a height, else none. */
  private scrollBox(): HTMLElement | null {
    if (this.stickyHeader()) return this.rowGroup()?.nativeElement ?? null;
    if (this.maxHeight()) return this.grid()?.nativeElement ?? null;
    return null;
  }

  private measureBands(): void {
    const heights = this.bands().map((band) => band.nativeElement.getBoundingClientRect().height);
    this.bandHeight.set(Math.max(0, ...heights));
  }

  private unsectioned(): MibTableGroup<T> {
    return { key: UNSECTIONED, section: null, rows: this.rows().map((row) => this.rowEntry(row)) };
  }

  private rowEntry(row: T): MibTableEntry<T> {
    return { key: this.key(row), row };
  }

  /*
   A path rather than a plain field name, so a page writes what it means — `row.href` reads as the
   address of a row where `href` read as an address itself. The `row.` head is dropped because it
   only names what the path starts from, and what is left walks down as far as it is written.
  */
  private field(row: T, path: string): string {
    const value = path
      .replace(/^row\./, '')
      .split('.')
      .reduce<unknown>((step, part) => (step == null ? undefined : (step as Record<string, unknown>)[part]), row);
    return value === null || value === undefined ? '' : String(value);
  }
}
